import React from 'react';
import { Share2, Twitter, Linkedin, Facebook, Link } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

interface SocialShareProps {
  title: string;
  url: string;
  description?: string;
  className?: string;
}

export default function SocialShare({ title, url, description, className = '' }: SocialShareProps) {
  const fullUrl = url.startsWith('http') ? url : `https://recruteurs.io${url}`;
  const encodedTitle = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(fullUrl);
  const encodedDescription = encodeURIComponent(description || title);

  const shareLinks = [
    {
      name: 'Twitter',
      icon: <Twitter size={18} />,
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: 'hover:bg-blue-50 hover:text-blue-600',
      bgColor: 'bg-blue-500'
    },
    {
      name: 'LinkedIn',
      icon: <Linkedin size={18} />,
      url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`,
      color: 'hover:bg-blue-50 hover:text-blue-700',
      bgColor: 'bg-blue-700'
    },
    {
      name: 'Facebook',
      icon: <Facebook size={18} />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: 'hover:bg-blue-50 hover:text-blue-800',
      bgColor: 'bg-blue-600'
    }
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Lien copié dans le presse-papiers !');
    } catch (err) {
      toast.error('Erreur lors de la copie du lien');
    }
  };

  const handleShare = (shareUrl: string, platform: string) => {
    window.open(shareUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
    
    // Analytics tracking (optionnel)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'share', {
        method: platform,
        content_type: 'article',
        item_id: url
      });
    }
  };

  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Share2 size={20} className="text-neutral-600" />
        <span className="text-sm font-medium text-neutral-700">Partager cet article</span>
      </div>
      
      <div className="flex items-center gap-3">
        {shareLinks.map((link, index) => (
          <motion.button
            key={link.name}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => handleShare(link.url, link.name)}
            className={`flex items-center justify-center w-10 h-10 rounded-full border border-neutral-200 transition-all duration-200 ${link.color} hover:scale-105 hover:shadow-md`}
            title={`Partager sur ${link.name}`}
            aria-label={`Partager sur ${link.name}`}
          >
            {link.icon}
          </motion.button>
        ))}
        
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          onClick={copyToClipboard}
          className="flex items-center justify-center w-10 h-10 rounded-full border border-neutral-200 hover:bg-neutral-50 hover:text-neutral-700 transition-all duration-200 hover:scale-105 hover:shadow-md"
          title="Copier le lien"
          aria-label="Copier le lien"
        >
          <Link size={18} />
        </motion.button>
      </div>
    </div>
  );
}